import { createContext, type ReactNode, use, useMemo, useRef } from "react";
import type { ApiResult, FetchResponse } from "../api";
import { useAuth } from "./AuthProvider";

// ---------------------------------------------------------------------------
// ApiFetch type
// ---------------------------------------------------------------------------

/**
 * Signature for the context-provided fetch function.
 *
 * Wraps the native `fetch` with:
 * - Automatic bearer-token injection from OIDC context.
 * - Response parsing into {@link ApiResult}.
 * - Never-throwing semantics — network errors are captured as
 *   `{ completed: false }` rather than thrown.
 */
export type ApiFetch = <T = void>(
  url: string,
  options?: RequestInit,
) => Promise<FetchResponse<T>>;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * React context holding the {@link ApiFetch} function.
 *
 * Exported so test harnesses can inject a mock implementation directly
 * via `<CheatsheetzFetchContext value={mockApiFetch}>`.
 */
export const CheatsheetzFetchContext = createContext<ApiFetch | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the {@link ApiFetch} function from the nearest
 * {@link CheatsheetzFetchProvider}.
 *
 * @throws When called outside a provider.
 */
export function useApiFetch(): ApiFetch {
  const apiFetch = use(CheatsheetzFetchContext);
  if (!apiFetch) {
    throw new Error("useApiFetch must be used within a CheatsheetzFetchProvider");
  }
  return apiFetch;
}

// ---------------------------------------------------------------------------
// Fetch status context — session-expiry notifications
// ---------------------------------------------------------------------------

/**
 * Integration surface exposed by {@link useApiFetchStatus}.
 *
 * Child providers (e.g. {@link SessionProvider}) use this to subscribe to
 * lifecycle events that the fetch layer detects but cannot handle itself
 * because it sits above the session provider in the component tree.
 */
export interface ApiFetchStatus {
  /**
   * Register a callback invoked when a 401 response cannot be recovered
   * via token refresh.  Returns an unsubscribe function.
   */
  onSessionExpired: (handler: () => void) => () => void;
}

const ApiFetchStatusContext = createContext<ApiFetchStatus | null>(null);

/**
 * Returns the {@link ApiFetchStatus} from the nearest
 * {@link CheatsheetzFetchProvider}.
 *
 * @throws When called outside a provider.
 */
export function useApiFetchStatus(): ApiFetchStatus {
  const status = use(ApiFetchStatusContext);
  if (!status) {
    throw new Error("useApiFetchStatus must be used within a CheatsheetzFetchProvider");
  }
  return status;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function CheatsheetzFetchProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useAuth();
  const sessionExpiredHandlerRef = useRef<(() => void) | null>(null);

  // Store getAccessToken in a ref so the memoized apiFetch always uses the latest
  const getTokenRef = useRef(getAccessToken);
  getTokenRef.current = getAccessToken;

  const apiFetch: ApiFetch = useMemo(() => {
    return async function apiFetchImpl<T>(
      url: string,
      options: RequestInit = {},
    ): Promise<FetchResponse<T>> {
      const token = getTokenRef.current();

      let response: Response;
      try {
        response = await doFetch(url, options, token);
      } catch (error) {
        return {
          completed: false,
          error: error instanceof Error ? error.message : "Network error",
        };
      }

      // On 401, notify session expired handler (OIDC context handles token refresh)
      if (response.status === 401 && token) {
        sessionExpiredHandlerRef.current?.();
        return {
          completed: true,
          response: createErrorResult<T>(401, undefined, [
            "Session expired. Please log in again.",
          ]),
        };
      }

      return { completed: true, response: await parseResponse<T>(response) };
    };
  }, []);

  const statusValue = useMemo<ApiFetchStatus>(
    () => ({
      onSessionExpired: (handler: () => void) => {
        sessionExpiredHandlerRef.current = handler;
        return () => {
          if (sessionExpiredHandlerRef.current === handler) {
            sessionExpiredHandlerRef.current = null;
          }
        };
      },
    }),
    [],
  );

  return (
    <CheatsheetzFetchContext value={apiFetch}>
      <ApiFetchStatusContext value={statusValue}>{children}</ApiFetchStatusContext>
    </CheatsheetzFetchContext>
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function doFetch(
  url: string,
  options: RequestInit,
  token: string | undefined,
): Promise<Response> {
  const headers = new Headers(options.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }

  return globalThis.fetch(url, { ...options, headers });
}

async function parseResponse<T>(response: Response): Promise<ApiResult<T>> {
  // 204 No Content — no body to parse
  if (response.status === 204) {
    return {
      success: true,
      status: 204,
      data: undefined as T,
      errors: [],
      warnings: [],
      informations: [],
    };
  }

  // All API responses use a consistent IApiResult envelope:
  // { success, status, data, errors, warnings, informations }
  let body: Record<string, unknown>;
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    // Response wasn't valid JSON — synthesize a client-side error result.
    return createErrorResult<T>(response.status, undefined, [
      "An unexpected error occurred.",
    ]);
  }

  const success = body.success === true;
  const status = typeof body.status === "number" ? body.status : response.status;
  const data = body.data as T | undefined;
  const errors = Array.isArray(body.errors) ? (body.errors as string[]) : [];
  const warnings = Array.isArray(body.warnings) ? (body.warnings as string[]) : [];
  const informations = Array.isArray(body.informations)
    ? (body.informations as string[])
    : [];

  return {
    success,
    status,
    data,
    errors,
    warnings,
    informations,
  };
}

function createErrorResult<T>(
  status: number,
  data: T | undefined,
  errors: string[],
): ApiResult<T> {
  return {
    success: false,
    status,
    data,
    errors,
    warnings: [],
    informations: [],
  };
}
